import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import { Box, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FLAG_WINDOW_MS } from '../../redux/features/issueReport/issueReport.slice';
import Colors from '../../utils/color';

interface FlagWindowDialProps {
  /** Absolute deadline, or null while paused. */
  expiresAt: number | null;
  /** Banked remainder while paused. */
  remainingMs: number | null;
  isPaused: boolean;
}

const DIAL_SIZE = 56;

/**
 * The countdown. DISPLAY ONLY — it says one thing: *this panel closes in N seconds.*
 *
 * ## It used to mean two things, and no longer can
 *
 * HITL-1501 put Undo inside the ring, because Undo and auto-close were the same
 * deadline off the same anchor (`created_at + FLAG_WINDOW_MS`) and rendering them
 * separately could only ever show the agent two numbers for one moment.
 *
 * HITL-1573 breaks that identity. Closing the panel is no longer terminal — the report
 * is reachable afterwards from the Issue button (HITL-1574) and the task-router lobby
 * (HITL-1575) — so the deadline stopped bounding the decision and now bounds only the
 * panel. `Discard` moved out to the header row as a peer of `Later`, which is where a
 * destructive action belongs once it is not racing a clock, and the ring narrowed to
 * its one remaining meaning.
 *
 * The number and the sweep are both DERIVED from `expiresAt`; the interval below only
 * forces a re-render. A throttled tab therefore shows a truthful remaining time
 * rather than a stretched one.
 *
 * ## The paused state needs static signals
 *
 * A frozen ring is indistinguishable from a running one at a glance — motion is the
 * only difference, and there is none in a still frame. So pausing also shifts the
 * numeral to amber and swaps in a pause glyph. Exactly two signals: fewer and the
 * hold is invisible, more (dashed arc, halo, badge) and it reads as an error.
 */
const FlagWindowDial = ({
  expiresAt,
  remainingMs,
  isPaused,
}: FlagWindowDialProps) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (isPaused || !expiresAt) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [isPaused, expiresAt]);

  const msLeft = isPaused
    ? remainingMs ?? 0
    : Math.max(0, (expiresAt ?? 0) - Date.now());
  const secondsLeft = Math.max(0, Math.ceil(msLeft / 1000));
  const progress = Math.max(0, Math.min(100, (msLeft / FLAG_WINDOW_MS) * 100));

  return (
    <Box
      sx={{
        position: 'relative',
        width: DIAL_SIZE,
        height: DIAL_SIZE,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      data-testid="flag-window-dial"
      data-paused={isPaused ? 'true' : 'false'}
      // `timer` rather than a bare aria-label on a generic Box, which assistive
      // technology is free to ignore. It also carries an implicit `aria-live="off"`,
      // so this does NOT announce itself once a second while the agent is trying to
      // read the taxonomy — and the label makes the ring's one meaning explicit
      // instead of leaving a screen reader to voice a bare numeral.
      role="timer"
      aria-label={`Panel closes in ${secondsLeft} seconds`}
    >
      {/* Track, so the depleted arc is legible rather than absent. */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={DIAL_SIZE}
        thickness={3}
        sx={{
          position: 'absolute',
          color: 'rgba(255,255,255,0.18)',
          pointerEvents: 'none',
        }}
      />
      <CircularProgress
        variant="determinate"
        value={progress}
        size={DIAL_SIZE}
        thickness={3}
        sx={{
          position: 'absolute',
          color: isPaused ? Colors.amber : Colors.green,
          pointerEvents: 'none',
        }}
      />
      {/* The label above speaks for the whole dial, so the numeral itself is hidden
          rather than read out a second time in a less useful form. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '1px',
          color: isPaused ? Colors.amber : Colors.white,
          fontWeight: 700,
          fontSize: '0.85rem',
          lineHeight: 1,
        }}
      >
        {isPaused && <PauseRoundedIcon sx={{ fontSize: 12 }} />}
        {secondsLeft}
      </Box>
    </Box>
  );
};

/**
 * Memoized: this re-renders once a second by design, and its host re-renders on every
 * store change, so the two must not multiply.
 */
export default React.memo(FlagWindowDial);
